import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Calendar,
  Info,
  ShieldAlert,
  Pill,
  Trash2,
  Plus,
  Edit2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ContraceptionProduct {
  supplyType: string;
  productPrescribed: string;
  packDispensed: string;
  numberOfPacks: string;
  doseDuration: string;
  daysSupply: string;
  doseRegimenType: string;
  tailoredDoseOption: string;
  doseDirections: string;
  additionalInstructions: string;
}

export interface NHSContraceptionFormData {
  // Consent
  patientConsent: "yes" | "no" | "";

  // Consultation Details
  consultationDate: string;
  consultationTime: string;
  consultationMethod: "face-to-face" | "telephone" | "video" | "";
  patientAccompanied: "yes" | "no" | "";
  accompaniedRelationship: string;
  accompaniedRelationshipOther: string;

  // Safeguarding
  safeguardingConcerns: "yes" | "no" | "";
  safeguardingDetails: string;
  followedSafeguardingPolicy: "yes" | "no" | "";
  patientUnder16: "yes" | "no" | "";
  fraserCompetencyAssessed: "yes" | "no" | "";
  coercionConcern: "yes" | "no" | "";
  coercionDetails: string;

  // Service Type
  serviceType: "oral-contraception" | "oral-emergency-contraception" | "";

  // Clinical Checks – Oral Contraception
  ocType: "initiation" | "ongoing-monitoring" | "";
  ocServiceReason: string;
  currentlyUsingContraception: "yes" | "no" | "";
  currentContraceptionMethod: string;
  ocSupplyType: "first-supply" | "repeat-supply" | "";
  ocGapInCycle: "yes" | "no" | "";
  ncrsChecked: "yes" | "no" | "";
  epsChecked: "yes" | "no" | "";
  newSymptomsOrSideEffects: "yes" | "no" | "";
  newSymptomsDetails: string;
  relevantAllergies: "yes" | "no" | "";
  relevantAllergiesDetails: string;
  relevantMedicalHistory: "yes" | "no" | "";
  relevantMedicalHistoryDetails: string;

  // Oral Contraception Type (pill type)
  ocPillType: "combined" | "progestogen-only" | "";

  // Vital Readings (Combined OC)
  vitalsBpSystolic: string;
  vitalsBpDiastolic: string;
  vitalsWeight: string;
  vitalsWeightUnit: "kg" | "lbs";
  vitalsHeight: string;
  vitalsHeightUnit: "cm" | "ft/in";
  vitalsHeightFeet: string;
  vitalsHeightInches: string;

  // Supply Details (OC path)
  ocSupplied: "yes" | "no" | "";
  noSupplyOutcome: string;
  noSupplyReason: string;
  suppliedProducts: ContraceptionProduct[];

  // Oral Emergency Contraception – Service Type sub-fields
  oecServiceReason: string;

  // Clinical Checks – Oral Emergency Contraception
  upsiDate: string;
  upsiTime: string;
  timeSinceUpsi: string;
  patientWeight: string;
  oecMedicationSupplied: string;
  oecContraindicationsIdentified: "yes" | "no" | "";
  oecContraindicationsDetails: string;

  // Outcomes
  larcDiscussed: "yes" | "no" | "";
  signpostingRequired: "yes" | "no" | "";
  signpostingAction: "signposting" | "escalation" | "";
  outcomes: string;
  outcomesOther: string;
  referralDestination: string;
  referralDestinationOther: string;
  escalationDestination: string;
  escalationDestinationOther: string;
  onwardReferralReason: string;
  onwardReferralDate: string;
  onwardReferralTime: string;
  destinationOdsCode: string;

  // Advice & Notes
  adviceGiven: boolean;
  adviceProvidedTo: string;
  planAndRequestedActions: string;
  internalNotes: string;
}

interface NHSContraceptionFormProps {
  isDarkMode?: boolean;
  onSave?: (data: NHSContraceptionFormData) => void;
  onComplete?: (data: NHSContraceptionFormData) => void;
  initialData?: Partial<NHSContraceptionFormData>;
  nhsContraceptionFormLoading?: boolean;
  consultationStatus?: number;
}

export function NHSContraceptionForm({
  isDarkMode = false,
  onSave,
  onComplete,
  initialData = {},
  nhsContraceptionFormLoading,
  consultationStatus,
}: NHSContraceptionFormProps) {
  const [formData, setFormData] = useState<NHSContraceptionFormData>({
    patientConsent: initialData.patientConsent || "",
    consultationDate:
      initialData.consultationDate || new Date().toISOString().split("T")[0],
    consultationTime:
      initialData.consultationTime || new Date().toTimeString().slice(0, 5),
    consultationMethod: initialData.consultationMethod || "",
    patientAccompanied: initialData.patientAccompanied || "",
    accompaniedRelationship: initialData.accompaniedRelationship || "",
    accompaniedRelationshipOther:
      initialData.accompaniedRelationshipOther || "",
    safeguardingConcerns: initialData.safeguardingConcerns || "",
    safeguardingDetails: initialData.safeguardingDetails || "",
    followedSafeguardingPolicy: initialData.followedSafeguardingPolicy || "",
    patientUnder16: initialData.patientUnder16 || "",
    fraserCompetencyAssessed: initialData.fraserCompetencyAssessed || "",
    coercionConcern: initialData.coercionConcern || "",
    coercionDetails: initialData.coercionDetails || "",
    serviceType: initialData.serviceType || "",
    ocType: initialData.ocType || "",
    ocServiceReason: initialData.ocServiceReason || "",
    currentlyUsingContraception: initialData.currentlyUsingContraception || "",
    currentContraceptionMethod: initialData.currentContraceptionMethod || "",
    ocSupplyType: initialData.ocSupplyType || "",
    ocGapInCycle: initialData.ocGapInCycle || "",
    ncrsChecked: initialData.ncrsChecked || "",
    epsChecked: initialData.epsChecked || "",
    newSymptomsOrSideEffects: initialData.newSymptomsOrSideEffects || "",
    newSymptomsDetails: initialData.newSymptomsDetails || "",
    relevantAllergies: initialData.relevantAllergies || "",
    relevantAllergiesDetails: initialData.relevantAllergiesDetails || "",
    relevantMedicalHistory: initialData.relevantMedicalHistory || "",
    relevantMedicalHistoryDetails:
      initialData.relevantMedicalHistoryDetails || "",
    ocPillType: initialData.ocPillType || "",
    vitalsBpSystolic: initialData.vitalsBpSystolic || "",
    vitalsBpDiastolic: initialData.vitalsBpDiastolic || "",
    vitalsWeight: initialData.vitalsWeight || "",
    vitalsWeightUnit: initialData.vitalsWeightUnit || "kg",
    vitalsHeight: initialData.vitalsHeight || "",
    vitalsHeightUnit: initialData.vitalsHeightUnit || "cm",
    vitalsHeightFeet: initialData.vitalsHeightFeet || "",
    vitalsHeightInches: initialData.vitalsHeightInches || "",
    upsiDate: initialData.upsiDate || "",
    upsiTime: initialData.upsiTime || "",
    timeSinceUpsi: initialData.timeSinceUpsi || "",
    patientWeight: initialData.patientWeight || "",
    ocSupplied: initialData.ocSupplied || "",
    noSupplyOutcome: initialData.noSupplyOutcome || "",
    noSupplyReason: initialData.noSupplyReason || "",
    suppliedProducts: initialData.suppliedProducts || [],
    oecServiceReason: initialData.oecServiceReason || "",
    oecMedicationSupplied: initialData.oecMedicationSupplied || "",
    oecContraindicationsIdentified:
      initialData.oecContraindicationsIdentified || "",
    oecContraindicationsDetails: initialData.oecContraindicationsDetails || "",
    larcDiscussed: initialData.larcDiscussed || "",
    signpostingRequired: initialData.signpostingRequired || "",
    signpostingAction: initialData.signpostingAction || "",
    outcomes: initialData.outcomes || "",
    outcomesOther: initialData.outcomesOther || "",
    referralDestination: initialData.referralDestination || "",
    referralDestinationOther: initialData.referralDestinationOther || "",
    escalationDestination: initialData.escalationDestination || "",
    escalationDestinationOther: initialData.escalationDestinationOther || "",
    onwardReferralReason: initialData.onwardReferralReason || "",
    onwardReferralDate: initialData.onwardReferralDate || "",
    onwardReferralTime: initialData.onwardReferralTime || "",
    destinationOdsCode: initialData.destinationOdsCode || "",
    adviceGiven: initialData.adviceGiven || false,
    adviceProvidedTo: initialData.adviceProvidedTo || "",
    planAndRequestedActions: initialData.planAndRequestedActions || "",
    internalNotes: initialData.internalNotes || "",
  });

  const [isDataSharingPopupOpen, setIsDataSharingPopupOpen] = useState(false);
  const [knowsOdsCode, setKnowsOdsCode] = useState(
    initialData?.destinationOdsCode ? "yes" : "",
  );
  const [isProductPopupOpen, setIsProductPopupOpen] = useState(false);
  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(
    null,
  );
  const [editingProduct, setEditingProduct] = useState<ContraceptionProduct>({
    supplyType: "pgd",
    productPrescribed: "",
    packDispensed: "",
    numberOfPacks: "",
    doseDuration: "",
    daysSupply: "",
    doseRegimenType: "",
    tailoredDoseOption: "",
    doseDirections: "Take one tablet daily at the same time each day",
    additionalInstructions: "",
  });

  const handleChange = (field: keyof NHSContraceptionFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSave) onSave(formData);
    if (onComplete) onComplete(formData);
  };

  const hasOutcomeReferral =
    formData.signpostingRequired === "yes" &&
    (formData.signpostingAction === "signposting" ||
      formData.signpostingAction === "escalation");

  // ─── Reusable style helpers ────────────────────────────────────────────
  const card = `border rounded-2xl p-4 transition-colors duration-300 ${
    isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
  }`;
  const sectionBadge = `flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold transition-colors duration-300 ${
    isDarkMode ? "bg-white text-black" : "bg-black text-white"
  }`;
  const sectionTitle = `text-sm font-semibold transition-colors duration-300 ${
    isDarkMode ? "text-white" : "text-gray-900"
  }`;
  const labelCls = `text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`;
  const inputCls = isDarkMode
    ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
    : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400";
  const selectCls = isDarkMode
    ? "bg-gray-700 border-gray-600 text-white"
    : "bg-white border-gray-300 text-gray-900";
  const selectContentCls = isDarkMode
    ? "bg-gray-700 border-gray-600"
    : "bg-white border-gray-300";
  const radioCls = isDarkMode
    ? "border-gray-600 text-white"
    : "border-gray-300 text-black";
  const radioLabelCls = `text-sm cursor-pointer ${
    isDarkMode ? "text-gray-300" : "text-gray-700"
  }`;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* ── Section 1: Patient Consent ─────────────────────────────────── */}
      <div className={card}>
        <div className="flex items-center gap-3 mb-4">
          <div className={sectionBadge}>1</div>
          <h3 className={sectionTitle}>Patient Consent</h3>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className={labelCls}>
              Does the patient consent to this consultation?
            </Label>
            <button
              type="button"
              onClick={() => setIsDataSharingPopupOpen(true)}
              className={`flex items-center gap-1 text-xs font-medium hover:underline ${
                isDarkMode ? "text-blue-400" : "text-blue-600"
              }`}
            >
              <Info className="w-3 h-3" />
              View Data Sharing Details
            </button>
          </div>
          <RadioGroup
            value={formData.patientConsent}
            onValueChange={(v) => handleChange("patientConsent", v)}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="yes"
                id="consent-yes"
                className={radioCls}
              />
              <label htmlFor="consent-yes" className={radioLabelCls}>
                Yes
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="consent-no" className={radioCls} />
              <label htmlFor="consent-no" className={radioLabelCls}>
                No
              </label>
            </div>
          </RadioGroup>
        </div>
      </div>

      {/* ── Gated sections (consent = yes) ────────────────────────────── */}
      {formData.patientConsent === "yes" && (
        <>
          {/* ── Section 2: Consultation Details ─────────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>2</div>
              <h3 className={sectionTitle}>Consultation Details</h3>
            </div>

            <div className="space-y-4">
              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cons-date" className={labelCls}>
                    Date
                  </Label>
                  <div className="relative">
                    <Input
                      id="cons-date"
                      type="date"
                      value={formData.consultationDate}
                      onChange={(e) =>
                        handleChange("consultationDate", e.target.value)
                      }
                      className={inputCls}
                    />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cons-time" className={labelCls}>
                    Time
                  </Label>
                  <Input
                    id="cons-time"
                    type="time"
                    value={formData.consultationTime}
                    onChange={(e) =>
                      handleChange("consultationTime", e.target.value)
                    }
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Method */}
              <div className="space-y-2">
                <Label htmlFor="cons-method" className={labelCls}>
                  Method of Consultation
                </Label>
                <Select
                  value={formData.consultationMethod}
                  onValueChange={(v) => handleChange("consultationMethod", v)}
                >
                  <SelectTrigger id="cons-method" className={selectCls}>
                    <SelectValue placeholder="Select consultation method" />
                  </SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    <SelectItem value="face-to-face">Face to Face</SelectItem>
                    <SelectItem value="telephone">Telephone</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Patient Accompanied */}
              <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Label
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
                >
                  Patient Accompanied
                </Label>
                <RadioGroup
                  value={formData.patientAccompanied}
                  onValueChange={(v) => {
                    handleChange("patientAccompanied", v);
                    if (v === "no") {
                      handleChange("accompaniedRelationship", "");
                      handleChange("accompaniedRelationshipOther", "");
                    }
                  }}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="yes"
                      id="accompanied-yes"
                      className={radioCls}
                    />
                    <label htmlFor="accompanied-yes" className={radioLabelCls}>
                      Yes
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="no"
                      id="accompanied-no"
                      className={radioCls}
                    />
                    <label htmlFor="accompanied-no" className={radioLabelCls}>
                      No
                    </label>
                  </div>
                </RadioGroup>

                {formData.patientAccompanied === "yes" && (
                  <div className="space-y-2 mt-3">
                    <Label htmlFor="relationship" className={labelCls}>
                      Relationship
                    </Label>
                    <Select
                      value={formData.accompaniedRelationship}
                      onValueChange={(v) => {
                        handleChange("accompaniedRelationship", v);
                        if (v !== "other")
                          handleChange("accompaniedRelationshipOther", "");
                      }}
                    >
                      <SelectTrigger id="relationship" className={selectCls}>
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent className={selectContentCls}>
                        <SelectItem value="parent">Parent</SelectItem>
                        <SelectItem value="guardian">Guardian</SelectItem>
                        <SelectItem value="relative">Relative</SelectItem>
                        <SelectItem value="carer">Carer</SelectItem>
                        <SelectItem value="advocate">Advocate</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.patientAccompanied === "yes" &&
                  formData.accompaniedRelationship === "other" && (
                    <div className="space-y-2 mt-3">
                      <Label htmlFor="relationship-other" className={labelCls}>
                        Please specify
                      </Label>
                      <Input
                        id="relationship-other"
                        type="text"
                        placeholder="Specify relationship"
                        value={formData.accompaniedRelationshipOther}
                        onChange={(e) =>
                          handleChange(
                            "accompaniedRelationshipOther",
                            e.target.value,
                          )
                        }
                        className={inputCls}
                      />
                    </div>
                  )}
              </div>
            </div>
          </div>

          {/* ── Section 3: Safeguarding ─────────────────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>3</div>
              <h3 className={sectionTitle}>Safeguarding</h3>
            </div>

            <div className="space-y-5">
              <Alert
                className={`${
                  isDarkMode
                    ? "bg-amber-900/20 border-amber-700 text-amber-200"
                    : "bg-amber-50 border-amber-300 text-amber-900"
                }`}
              >
                <ShieldAlert className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  Consider safeguarding at all points of the consultation. If
                  concerns arise, follow your local safeguarding procedures and
                  document appropriately.
                </AlertDescription>
              </Alert>

              {/* Any safeguarding concerns? */}
              <div className="space-y-2">
                <Label className={labelCls}>
                  Are there any safeguarding concerns?
                </Label>
                <RadioGroup
                  value={formData.safeguardingConcerns}
                  onValueChange={(v) => {
                    handleChange("safeguardingConcerns", v);
                    if (v === "no") {
                      handleChange("safeguardingDetails", "");
                      handleChange("followedSafeguardingPolicy", "");
                    }
                  }}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="yes"
                      id="safeguarding-yes"
                      className={radioCls}
                    />
                    <label htmlFor="safeguarding-yes" className={radioLabelCls}>
                      Yes
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="no"
                      id="safeguarding-no"
                      className={radioCls}
                    />
                    <label htmlFor="safeguarding-no" className={radioLabelCls}>
                      No
                    </label>
                  </div>
                </RadioGroup>

                {formData.safeguardingConcerns === "yes" && (
                  <>
                    <div className="space-y-2 mt-3">
                      <Label className={labelCls}>
                        Have you followed the local safeguarding policy?
                      </Label>
                      <RadioGroup
                        value={formData.followedSafeguardingPolicy}
                        onValueChange={(v) =>
                          handleChange("followedSafeguardingPolicy", v)
                        }
                        className="flex gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="yes"
                            id="safeguarding-policy-yes"
                            className={radioCls}
                          />
                          <label
                            htmlFor="safeguarding-policy-yes"
                            className={radioLabelCls}
                          >
                            Yes
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="no"
                            id="safeguarding-policy-no"
                            className={radioCls}
                          />
                          <label
                            htmlFor="safeguarding-policy-no"
                            className={radioLabelCls}
                          >
                            No
                          </label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div className="space-y-2 mt-3">
                      <Label
                        htmlFor="safeguarding-details"
                        className={labelCls}
                      >
                        Please provide some details / narrative
                      </Label>
                      <Textarea
                        id="safeguarding-details"
                        placeholder="Describe safeguarding concerns..."
                        value={formData.safeguardingDetails}
                        onChange={(e) =>
                          handleChange("safeguardingDetails", e.target.value)
                        }
                        rows={3}
                        className={inputCls}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── Section 4: Service Type ──────────────────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>4</div>
              <h3 className={sectionTitle}>Service Type</h3>
            </div>

            <div className="space-y-3">
              <Label className={labelCls}>
                Select the contraception service being provided
              </Label>
              <RadioGroup
                value={formData.serviceType}
                onValueChange={(v) => {
                  handleChange("serviceType", v as any);
                  // Reset clinical check fields when switching service type
                  handleChange("ocType", "");
                  handleChange("ocServiceReason", "");
                  handleChange("currentlyUsingContraception", "");
                  handleChange("currentContraceptionMethod", "");
                  handleChange("ocSupplyType", "");
                  handleChange("ocGapInCycle", "");
                  handleChange("ncrsChecked", "");
                  handleChange("epsChecked", "");
                  handleChange("newSymptomsOrSideEffects", "");
                  handleChange("newSymptomsDetails", "");
                  handleChange("relevantAllergies", "");
                  handleChange("relevantAllergiesDetails", "");
                  handleChange("relevantMedicalHistory", "");
                  handleChange("relevantMedicalHistoryDetails", "");
                  handleChange("ocPillType", "");
                  handleChange("bpReading", "");
                  handleChange("ocContraindicationsIdentified", "");
                  handleChange("ocContraindicationsDetails", "");
                  handleChange("ocMedicationSupplied", "");
                  handleChange("ocDoseInstructions", "");
                  handleChange("upsiDate", "");
                  handleChange("upsiTime", "");
                  handleChange("timeSinceUpsi", "");
                  handleChange("patientWeight", "");
                  handleChange("oecMedicationSupplied", "");
                  handleChange("oecContraindicationsIdentified", "");
                  handleChange("oecContraindicationsDetails", "");
                  handleChange("oecServiceReason", "");
                }}
                className="flex flex-col gap-3"
              >
                <label
                  htmlFor="service-oc"
                  className={`flex items-start space-x-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                    formData.serviceType === "oral-contraception"
                      ? isDarkMode
                        ? "border-blue-500 bg-blue-900/20"
                        : "border-blue-500 bg-blue-50"
                      : isDarkMode
                        ? "border-gray-600 bg-gray-700/30"
                        : "border-gray-200 bg-gray-50/50"
                  }`}
                >
                  <RadioGroupItem
                    value="oral-contraception"
                    id="service-oc"
                    className={`mt-0.5 ${radioCls}`}
                  />
                  <div>
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-100" : "text-gray-900"
                      }`}
                    >
                      Oral Contraception
                    </span>
                    <p
                      className={`text-xs mt-0.5 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Supply of combined oral contraceptive pill or
                      progestogen-only pill
                    </p>
                  </div>
                </label>

                <label
                  htmlFor="service-oec"
                  className={`flex items-start space-x-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                    formData.serviceType === "oral-emergency-contraception"
                      ? isDarkMode
                        ? "border-blue-500 bg-blue-900/20"
                        : "border-blue-500 bg-blue-50"
                      : isDarkMode
                        ? "border-gray-600 bg-gray-700/30"
                        : "border-gray-200 bg-gray-50/50"
                  }`}
                >
                  <RadioGroupItem
                    value="oral-emergency-contraception"
                    id="service-oec"
                    className={`mt-0.5 ${radioCls}`}
                  />
                  <div>
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-100" : "text-gray-900"
                      }`}
                    >
                      Oral Emergency Contraception
                    </span>
                    <p
                      className={`text-xs mt-0.5 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Emergency hormonal contraception (e.g. Levonorgestrel,
                      Ulipristal Acetate)
                    </p>
                  </div>
                </label>
              </RadioGroup>

              {/* Info panel for Oral Contraception */}
              {formData.serviceType === "oral-contraception" && (
                <Alert
                  className={`mt-4 ${
                    isDarkMode
                      ? "bg-blue-900/20 border-blue-800 text-blue-200"
                      : "bg-blue-50 border-blue-200 text-blue-900"
                  }`}
                >
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-sm space-y-2">
                    <p>
                      <span className="font-semibold">Initiation:</span> where a
                      person wishes to start OC for the first time or needs to
                      restart OC following a pill free break. A person who is
                      being switched to an alternative pill following
                      consultation can also be considered as an initiation.
                    </p>
                    <p>
                      <span className="font-semibold">Ongoing monitoring:</span>{" "}
                      where a person has been supplied with OC by their general
                      practice, a sexual health clinic, or a pharmacy, and a
                      subsequent equivalent supply is needed. Their current
                      supply of OC should still be in use.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {/* Info panel for Oral Emergency Contraception */}
              {formData.serviceType === "oral-emergency-contraception" && (
                <Alert
                  className={`mt-4 ${
                    isDarkMode
                      ? "bg-orange-900/20 border-orange-700 text-orange-200"
                      : "bg-orange-50 border-orange-200 text-orange-900"
                  }`}
                >
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-sm space-y-2">
                    <p>
                      <span className="font-semibold">Initial Supply:</span> A
                      single dose of oral EC should be made for all oral EC
                      consultations. This should be 1 tablet unless the person
                      meets criteria outlined in the PGD that allow for an
                      alternative regimen.
                    </p>
                    <p>
                      <span className="font-semibold">Repeat Dose:</span> If a
                      person is sick within 3 hours of the initial dose of oral
                      EC being taken, a further supply of oral EC can be made by
                      the pharmacist or pharmacy technician. This should be
                      recorded as a separate consultation (and will be paid for
                      accordingly).
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {/* OC type / initiation sub-fields (within Service Type) */}
              {formData.serviceType === "oral-contraception" && (
                <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {/* Oral contraception type */}
                  <div className="space-y-2">
                    <Label className={labelCls}>Oral contraception type</Label>
                    <RadioGroup
                      value={formData.ocType}
                      onValueChange={(v) => {
                        handleChange("ocType", v as any);
                        handleChange("ocServiceReason", "");
                        handleChange("currentlyUsingContraception", "");
                        handleChange("currentContraceptionMethod", "");
                      }}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="initiation"
                          id="oc-type-initiation"
                          className={radioCls}
                        />
                        <label
                          htmlFor="oc-type-initiation"
                          className={radioLabelCls}
                        >
                          Initiation
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="ongoing-monitoring"
                          id="oc-type-ongoing"
                          className={radioCls}
                        />
                        <label
                          htmlFor="oc-type-ongoing"
                          className={radioLabelCls}
                        >
                          Ongoing Monitoring
                        </label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Sub-section shown when either type is selected */}
                  {(formData.ocType === "initiation" ||
                    formData.ocType === "ongoing-monitoring") && (
                    <div className="space-y-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                      {/* Service reason */}
                      <div className="space-y-2">
                        <Label
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          Oral contraception service reason
                        </Label>

                        {formData.ocType === "initiation" ? (
                          <RadioGroup
                            value={formData.ocServiceReason}
                            onValueChange={(v) =>
                              handleChange("ocServiceReason", v)
                            }
                            className="flex flex-col gap-3"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="first-time"
                                id="oc-reason-first"
                                className={radioCls}
                              />
                              <label
                                htmlFor="oc-reason-first"
                                className={radioLabelCls}
                              >
                                Initiation of oral contraception – First time
                                pill user
                              </label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="re-initiation"
                                id="oc-reason-reinit"
                                className={radioCls}
                              />
                              <label
                                htmlFor="oc-reason-reinit"
                                className={radioLabelCls}
                              >
                                Re-initiation of oral contraception – has been
                                on pill before
                              </label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="switching"
                                id="oc-reason-switch"
                                className={radioCls}
                              />
                              <label
                                htmlFor="oc-reason-switch"
                                className={radioLabelCls}
                              >
                                Switching oral contraception
                              </label>
                            </div>
                          </RadioGroup>
                        ) : (
                          <RadioGroup
                            value={formData.ocServiceReason}
                            onValueChange={(v) =>
                              handleChange("ocServiceReason", v)
                            }
                            className="flex flex-col gap-3"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="ongoing-gp-srhc"
                                id="oc-reason-gp-srhc"
                                className={radioCls}
                              />
                              <label
                                htmlFor="oc-reason-gp-srhc"
                                className={radioLabelCls}
                              >
                                Ongoing monitoring / supply – GP / SRHC
                                initiated
                              </label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="ongoing-pharmacist"
                                id="oc-reason-pharmacist"
                                className={radioCls}
                              />
                              <label
                                htmlFor="oc-reason-pharmacist"
                                className={radioLabelCls}
                              >
                                Ongoing monitoring / supply – Pharmacist
                                initiated
                              </label>
                            </div>
                          </RadioGroup>
                        )}
                      </div>

                      {/* Currently using contraception? */}
                      <div className="space-y-2">
                        <Label className={labelCls}>
                          Is the patient currently using any form of
                          contraception?
                        </Label>
                        <RadioGroup
                          value={formData.currentlyUsingContraception}
                          onValueChange={(v) => {
                            handleChange(
                              "currentlyUsingContraception",
                              v as any,
                            );
                            if (v === "no")
                              handleChange("currentContraceptionMethod", "");
                          }}
                          className="flex gap-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="yes"
                              id="current-contra-yes"
                              className={radioCls}
                            />
                            <label
                              htmlFor="current-contra-yes"
                              className={radioLabelCls}
                            >
                              Yes
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="no"
                              id="current-contra-no"
                              className={radioCls}
                            />
                            <label
                              htmlFor="current-contra-no"
                              className={radioLabelCls}
                            >
                              No
                            </label>
                          </div>
                        </RadioGroup>

                        {formData.currentlyUsingContraception === "yes" && (
                          <div className="space-y-2 mt-3">
                            <Label
                              htmlFor="current-method"
                              className={labelCls}
                            >
                              Current method(s) of contraception used by patient
                            </Label>
                            <Select
                              value={formData.currentContraceptionMethod}
                              onValueChange={(v) =>
                                handleChange("currentContraceptionMethod", v)
                              }
                            >
                              <SelectTrigger
                                id="current-method"
                                className={selectCls}
                              >
                                <SelectValue placeholder="Select current method" />
                              </SelectTrigger>
                              <SelectContent className={selectContentCls}>
                                <SelectItem value="combined-pill">
                                  Combined oral contraceptive pill
                                </SelectItem>
                                <SelectItem value="progestogen-only-pill">
                                  Progestogen-only pill
                                </SelectItem>
                                <SelectItem value="condoms">
                                  Condoms (male or female)
                                </SelectItem>
                                <SelectItem value="implant">
                                  Contraceptive implant
                                </SelectItem>
                                <SelectItem value="injection">
                                  Contraceptive injection
                                </SelectItem>
                                <SelectItem value="iud">
                                  Intrauterine device (IUD / coil)
                                </SelectItem>
                                <SelectItem value="ius">
                                  Intrauterine system (IUS / hormonal coil)
                                </SelectItem>
                                <SelectItem value="patch">
                                  Contraceptive patch
                                </SelectItem>
                                <SelectItem value="vaginal-ring">
                                  Vaginal ring
                                </SelectItem>
                                <SelectItem value="diaphragm">
                                  Diaphragm or cap
                                </SelectItem>
                                <SelectItem value="natural-planning">
                                  Natural family planning
                                </SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* OEC service reason + contraception sub-fields (within Service Type) */}
              {formData.serviceType === "oral-emergency-contraception" && (
                <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {/* Service reason */}
                  <div className="space-y-2">
                    <Label
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                    >
                      Service Reason
                    </Label>
                    <RadioGroup
                      value={formData.oecServiceReason}
                      onValueChange={(v) => handleChange("oecServiceReason", v)}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="initial-supply"
                          id="oec-reason-initial"
                          className={radioCls}
                        />
                        <label
                          htmlFor="oec-reason-initial"
                          className={radioLabelCls}
                        >
                          Initial Supply
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="repeat-dose"
                          id="oec-reason-repeat"
                          className={radioCls}
                        />
                        <label
                          htmlFor="oec-reason-repeat"
                          className={radioLabelCls}
                        >
                          Repeat Dose
                        </label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Currently using contraception? */}
                  <div className="space-y-2">
                    <Label className={labelCls}>
                      Is the patient currently using any form of contraception?
                    </Label>
                    <RadioGroup
                      value={formData.currentlyUsingContraception}
                      onValueChange={(v) => {
                        handleChange("currentlyUsingContraception", v as any);
                        if (v === "no")
                          handleChange("currentContraceptionMethod", "");
                      }}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="yes"
                          id="oec-current-contra-yes"
                          className={radioCls}
                        />
                        <label
                          htmlFor="oec-current-contra-yes"
                          className={radioLabelCls}
                        >
                          Yes
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="no"
                          id="oec-current-contra-no"
                          className={radioCls}
                        />
                        <label
                          htmlFor="oec-current-contra-no"
                          className={radioLabelCls}
                        >
                          No
                        </label>
                      </div>
                    </RadioGroup>

                    {formData.currentlyUsingContraception === "yes" && (
                      <div className="space-y-2 mt-3">
                        <Label
                          htmlFor="oec-current-method"
                          className={labelCls}
                        >
                          Current method(s) of contraception used by patient
                        </Label>
                        <Select
                          value={formData.currentContraceptionMethod}
                          onValueChange={(v) =>
                            handleChange("currentContraceptionMethod", v)
                          }
                        >
                          <SelectTrigger
                            id="oec-current-method"
                            className={selectCls}
                          >
                            <SelectValue placeholder="Select current method" />
                          </SelectTrigger>
                          <SelectContent className={selectContentCls}>
                            <SelectItem value="combined-pill">
                              Combined oral contraceptive pill
                            </SelectItem>
                            <SelectItem value="progestogen-only-pill">
                              Progestogen-only pill
                            </SelectItem>
                            <SelectItem value="condoms">
                              Condoms (male or female)
                            </SelectItem>
                            <SelectItem value="implant">
                              Contraceptive implant
                            </SelectItem>
                            <SelectItem value="injection">
                              Contraceptive injection
                            </SelectItem>
                            <SelectItem value="iud">
                              Intrauterine device (IUD / coil)
                            </SelectItem>
                            <SelectItem value="ius">
                              Intrauterine system (IUS / hormonal coil)
                            </SelectItem>
                            <SelectItem value="patch">
                              Contraceptive patch
                            </SelectItem>
                            <SelectItem value="vaginal-ring">
                              Vaginal ring
                            </SelectItem>
                            <SelectItem value="diaphragm">
                              Diaphragm or cap
                            </SelectItem>
                            <SelectItem value="natural-planning">
                              Natural family planning
                            </SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Section 5: Clinical Checks (shown only once service type selected) */}
          {formData.serviceType !== "" && (
            <div className={card}>
              <div className="flex items-center gap-3 mb-4">
                <div className={sectionBadge}>5</div>
                <h3 className={sectionTitle}>Clinical Checks</h3>
              </div>

              {/* ── Oral Contraception Checks ─────────────────────────── */}
              {formData.serviceType === "oral-contraception" && (
                <div className="space-y-5">
                  {/* Gap in oral contraception */}
                  <div className="space-y-2">
                    <Label className={labelCls}>
                      Has the patient had a gap of any length in their oral
                      contraception?
                    </Label>
                    <RadioGroup
                      value={formData.ocGapInCycle}
                      onValueChange={(v) =>
                        handleChange("ocGapInCycle", v as any)
                      }
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="yes"
                          id="oc-gap-yes"
                          className={radioCls}
                        />
                        <label htmlFor="oc-gap-yes" className={radioLabelCls}>
                          Yes
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="no"
                          id="oc-gap-no"
                          className={radioCls}
                        />
                        <label htmlFor="oc-gap-no" className={radioLabelCls}>
                          No
                        </label>
                      </div>
                    </RadioGroup>

                    {formData.ocGapInCycle === "yes" && (
                      <Alert
                        className={`mt-3 ${
                          isDarkMode
                            ? "bg-amber-900/20 border-amber-700 text-amber-200"
                            : "bg-amber-50 border-amber-300 text-amber-900"
                        }`}
                      >
                        <Info className="h-4 w-4" />
                        <AlertDescription className="text-sm">
                          People who have had a gap (of any duration in length)
                          in their OC cycle are not eligible for ongoing
                          monitoring. If this is the case, they may be suitable
                          for re-initiation.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>

                  {/* NCRS checked */}
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Label className={labelCls}>
                      Has the National Care Records Service been checked?
                    </Label>
                    <RadioGroup
                      value={formData.ncrsChecked}
                      onValueChange={(v) =>
                        handleChange("ncrsChecked", v as any)
                      }
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="yes"
                          id="ncrs-yes"
                          className={radioCls}
                        />
                        <label htmlFor="ncrs-yes" className={radioLabelCls}>
                          Yes
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="no"
                          id="ncrs-no"
                          className={radioCls}
                        />
                        <label htmlFor="ncrs-no" className={radioLabelCls}>
                          No
                        </label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* EPS checked */}
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Label className={labelCls}>
                      Has the Electronic Prescription Service been checked for a
                      current contraceptive prescription?
                    </Label>
                    <RadioGroup
                      value={formData.epsChecked}
                      onValueChange={(v) =>
                        handleChange("epsChecked", v as any)
                      }
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="yes"
                          id="eps-yes"
                          className={radioCls}
                        />
                        <label htmlFor="eps-yes" className={radioLabelCls}>
                          Yes
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="no"
                          id="eps-no"
                          className={radioCls}
                        />
                        <label htmlFor="eps-no" className={radioLabelCls}>
                          No
                        </label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* New symptoms or side effects */}
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Label className={labelCls}>
                      Has the patient noticed any new symptoms or side effects
                      since their last GP appointment?
                    </Label>
                    <RadioGroup
                      value={formData.newSymptomsOrSideEffects}
                      onValueChange={(v) => {
                        handleChange("newSymptomsOrSideEffects", v as any);
                        if (v === "no") handleChange("newSymptomsDetails", "");
                      }}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="yes"
                          id="symptoms-yes"
                          className={radioCls}
                        />
                        <label htmlFor="symptoms-yes" className={radioLabelCls}>
                          Yes
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="no"
                          id="symptoms-no"
                          className={radioCls}
                        />
                        <label htmlFor="symptoms-no" className={radioLabelCls}>
                          No
                        </label>
                      </div>
                    </RadioGroup>
                    {formData.newSymptomsOrSideEffects === "yes" && (
                      <div className="space-y-2 mt-3">
                        <Label htmlFor="symptoms-details" className={labelCls}>
                          Please provide details
                        </Label>
                        <Textarea
                          id="symptoms-details"
                          placeholder="Describe symptoms or side effects..."
                          value={formData.newSymptomsDetails}
                          onChange={(e) =>
                            handleChange("newSymptomsDetails", e.target.value)
                          }
                          rows={3}
                          className={inputCls}
                        />
                      </div>
                    )}
                  </div>

                  {/* Relevant allergies */}
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Label className={labelCls}>
                      Does the patient have any relevant allergies or adverse
                      reactions?
                    </Label>
                    <RadioGroup
                      value={formData.relevantAllergies}
                      onValueChange={(v) => {
                        handleChange("relevantAllergies", v as any);
                        if (v === "no")
                          handleChange("relevantAllergiesDetails", "");
                      }}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="yes"
                          id="allergies-yes"
                          className={radioCls}
                        />
                        <label
                          htmlFor="allergies-yes"
                          className={radioLabelCls}
                        >
                          Yes
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="no"
                          id="allergies-no"
                          className={radioCls}
                        />
                        <label htmlFor="allergies-no" className={radioLabelCls}>
                          No
                        </label>
                      </div>
                    </RadioGroup>
                    {formData.relevantAllergies === "yes" && (
                      <div className="space-y-2 mt-3">
                        <Label htmlFor="allergies-details" className={labelCls}>
                          Please provide details
                        </Label>
                        <Textarea
                          id="allergies-details"
                          placeholder="Describe allergies or adverse reactions..."
                          value={formData.relevantAllergiesDetails}
                          onChange={(e) =>
                            handleChange(
                              "relevantAllergiesDetails",
                              e.target.value,
                            )
                          }
                          rows={3}
                          className={inputCls}
                        />
                      </div>
                    )}
                  </div>

                  {/* Relevant medical history */}
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Label className={labelCls}>
                      Does the patient have any relevant past medical, surgical,
                      and mental health history?
                    </Label>
                    <RadioGroup
                      value={formData.relevantMedicalHistory}
                      onValueChange={(v) => {
                        handleChange("relevantMedicalHistory", v as any);
                        if (v === "no")
                          handleChange("relevantMedicalHistoryDetails", "");
                      }}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="yes"
                          id="medhist-yes"
                          className={radioCls}
                        />
                        <label htmlFor="medhist-yes" className={radioLabelCls}>
                          Yes
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="no"
                          id="medhist-no"
                          className={radioCls}
                        />
                        <label htmlFor="medhist-no" className={radioLabelCls}>
                          No
                        </label>
                      </div>
                    </RadioGroup>
                    {formData.relevantMedicalHistory === "yes" && (
                      <div className="space-y-2 mt-3">
                        <Label htmlFor="medhist-details" className={labelCls}>
                          Please provide details
                        </Label>
                        <Textarea
                          id="medhist-details"
                          placeholder="Describe relevant history..."
                          value={formData.relevantMedicalHistoryDetails}
                          onChange={(e) =>
                            handleChange(
                              "relevantMedicalHistoryDetails",
                              e.target.value,
                            )
                          }
                          rows={3}
                          className={inputCls}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Oral Emergency Contraception Checks ───────────────── */}
              {formData.serviceType === "oral-emergency-contraception" && (
                <div className="space-y-5">
                  {/* Date / Time of UPSI */}
                  <div className="space-y-2">
                    <Label
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                    >
                      Date and time of unprotected sexual intercourse (UPSI)
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="upsi-date" className={labelCls}>
                          Date of UPSI
                        </Label>
                        <div className="relative">
                          <Input
                            id="upsi-date"
                            type="date"
                            value={formData.upsiDate}
                            onChange={(e) =>
                              handleChange("upsiDate", e.target.value)
                            }
                            className={inputCls}
                          />
                          <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="upsi-time" className={labelCls}>
                          Time of UPSI
                        </Label>
                        <Input
                          id="upsi-time"
                          type="time"
                          value={formData.upsiTime}
                          onChange={(e) =>
                            handleChange("upsiTime", e.target.value)
                          }
                          className={inputCls}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Time since UPSI */}
                  <div className="space-y-2">
                    <Label htmlFor="time-since-upsi" className={labelCls}>
                      Time since UPSI (hours)
                    </Label>
                    <Input
                      id="time-since-upsi"
                      type="number"
                      min="0"
                      max="120"
                      placeholder="e.g. 36"
                      value={formData.timeSinceUpsi}
                      onChange={(e) =>
                        handleChange("timeSinceUpsi", e.target.value)
                      }
                      className={inputCls}
                    />
                    <p
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Levonorgestrel is licensed up to 72 hours; Ulipristal
                      Acetate up to 120 hours after UPSI.
                    </p>
                  </div>

                  {/* Patient weight */}
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Label htmlFor="patient-weight" className={labelCls}>
                      Patient weight (kg){" "}
                      <span className="text-gray-400">(optional)</span>
                    </Label>
                    <Input
                      id="patient-weight"
                      type="text"
                      placeholder="e.g. 70"
                      value={formData.patientWeight}
                      onChange={(e) =>
                        handleChange("patientWeight", e.target.value)
                      }
                      className={inputCls}
                    />
                    <p
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Relevant for Levonorgestrel dosing if patient weighs more
                      than 70 kg or BMI &gt; 26.
                    </p>
                  </div>

                  {/* EHC supplied */}
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Label htmlFor="oec-medication" className={labelCls}>
                      <span className="flex items-center gap-1.5">
                        <Pill className="w-3.5 h-3.5" />
                        Emergency contraception supplied
                      </span>
                    </Label>
                    <Select
                      value={formData.oecMedicationSupplied}
                      onValueChange={(v) =>
                        handleChange("oecMedicationSupplied", v)
                      }
                    >
                      <SelectTrigger id="oec-medication" className={selectCls}>
                        <SelectValue placeholder="Select medication supplied" />
                      </SelectTrigger>
                      <SelectContent className={selectContentCls}>
                        <SelectItem value="levonorgestrel-1500">
                          Levonorgestrel 1500 mcg (Levonelle / generic)
                        </SelectItem>
                        <SelectItem value="ulipristal-30">
                          Ulipristal Acetate 30 mg (ellaOne)
                        </SelectItem>
                        <SelectItem value="copper-iud-referral">
                          Copper IUD – referred to another service
                        </SelectItem>
                        <SelectItem value="not-supplied">
                          Not supplied
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Contraindications */}
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Label className={labelCls}>
                      Any contraindications identified?
                    </Label>
                    <RadioGroup
                      value={formData.oecContraindicationsIdentified}
                      onValueChange={(v) => {
                        handleChange(
                          "oecContraindicationsIdentified",
                          v as any,
                        );
                        if (v === "no")
                          handleChange("oecContraindicationsDetails", "");
                      }}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="yes"
                          id="oec-ci-yes"
                          className={radioCls}
                        />
                        <label htmlFor="oec-ci-yes" className={radioLabelCls}>
                          Yes
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="no"
                          id="oec-ci-no"
                          className={radioCls}
                        />
                        <label htmlFor="oec-ci-no" className={radioLabelCls}>
                          No
                        </label>
                      </div>
                    </RadioGroup>

                    {formData.oecContraindicationsIdentified === "yes" && (
                      <div className="space-y-2 mt-3">
                        <Label htmlFor="oec-ci-details" className={labelCls}>
                          Please provide details
                        </Label>
                        <Textarea
                          id="oec-ci-details"
                          placeholder="Describe contraindications..."
                          value={formData.oecContraindicationsDetails}
                          onChange={(e) =>
                            handleChange(
                              "oecContraindicationsDetails",
                              e.target.value,
                            )
                          }
                          rows={3}
                          className={inputCls}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Section 6: Oral Contraception Type (only for OC) ────────── */}
          {formData.serviceType === "oral-contraception" && (
            <div className={card}>
              <div className="flex items-center gap-3 mb-4">
                <div className={sectionBadge}>6</div>
                <h3 className={sectionTitle}>Oral Contraception Type</h3>
              </div>

              <div className="space-y-3">
                <Label className={labelCls}>
                  Select the type of oral contraception
                </Label>
                <RadioGroup
                  value={formData.ocPillType}
                  onValueChange={(v) => handleChange("ocPillType", v as any)}
                  className="flex flex-col gap-3"
                >
                  <label
                    htmlFor="pill-combined"
                    className={`flex items-start space-x-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                      formData.ocPillType === "combined"
                        ? isDarkMode
                          ? "border-blue-500 bg-blue-900/20"
                          : "border-blue-500 bg-blue-50"
                        : isDarkMode
                          ? "border-gray-600 bg-gray-700/30"
                          : "border-gray-200 bg-gray-50/50"
                    }`}
                  >
                    <RadioGroupItem
                      value="combined"
                      id="pill-combined"
                      className={`mt-0.5 ${radioCls}`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-100" : "text-gray-900"
                      }`}
                    >
                      Combined Oral Contraceptive
                    </span>
                  </label>
                  <label
                    htmlFor="pill-pop"
                    className={`flex items-start space-x-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                      formData.ocPillType === "progestogen-only"
                        ? isDarkMode
                          ? "border-blue-500 bg-blue-900/20"
                          : "border-blue-500 bg-blue-50"
                        : isDarkMode
                          ? "border-gray-600 bg-gray-700/30"
                          : "border-gray-200 bg-gray-50/50"
                    }`}
                  >
                    <RadioGroupItem
                      value="progestogen-only"
                      id="pill-pop"
                      className={`mt-0.5 ${radioCls}`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-100" : "text-gray-900"
                      }`}
                    >
                      Progestogen Only Pill
                    </span>
                  </label>
                </RadioGroup>
              </div>
            </div>
          )}

          {/* ── Section 7: Vital Readings (Combined OC only) ──────────── */}
          {formData.serviceType === "oral-contraception" &&
            formData.ocPillType === "combined" && (
              <div className={card}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={sectionBadge}>7</div>
                  <h3 className={sectionTitle}>Vital Readings</h3>
                </div>

                <div className="space-y-5">
                  {/* Blood Pressure */}
                  <div className="space-y-2">
                    <Label
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                    >
                      Blood Pressure
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="bp-systolic" className={labelCls}>
                          Systolic (mmHg)
                        </Label>
                        <Input
                          id="bp-systolic"
                          type="number"
                          min="60"
                          max="250"
                          placeholder="e.g. 120"
                          value={formData.vitalsBpSystolic}
                          onChange={(e) =>
                            handleChange("vitalsBpSystolic", e.target.value)
                          }
                          className={inputCls}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bp-diastolic" className={labelCls}>
                          Diastolic (mmHg)
                        </Label>
                        <Input
                          id="bp-diastolic"
                          type="number"
                          min="30"
                          max="150"
                          placeholder="e.g. 80"
                          value={formData.vitalsBpDiastolic}
                          onChange={(e) =>
                            handleChange("vitalsBpDiastolic", e.target.value)
                          }
                          className={inputCls}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Weight */}
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Label className={labelCls}>Weight</Label>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Input
                          id="vitals-weight"
                          type="number"
                          min="0"
                          placeholder={
                            formData.vitalsWeightUnit === "kg"
                              ? "e.g. 70"
                              : "e.g. 154"
                          }
                          value={formData.vitalsWeight}
                          onChange={(e) =>
                            handleChange("vitalsWeight", e.target.value)
                          }
                          className={inputCls}
                        />
                      </div>
                      <Select
                        value={formData.vitalsWeightUnit}
                        onValueChange={(v) =>
                          handleChange("vitalsWeightUnit", v as any)
                        }
                      >
                        <SelectTrigger className={`w-24 ${selectCls}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={selectContentCls}>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="lbs">lbs</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Height */}
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Label className={labelCls}>Height</Label>
                    <div className="flex gap-3">
                      {formData.vitalsHeightUnit === "cm" ? (
                        <div className="flex-1">
                          <Input
                            id="vitals-height"
                            type="number"
                            min="0"
                            placeholder="e.g. 170"
                            value={formData.vitalsHeight}
                            onChange={(e) =>
                              handleChange("vitalsHeight", e.target.value)
                            }
                            className={inputCls}
                          />
                        </div>
                      ) : (
                        <div className="flex-1 flex gap-2">
                          <div className="flex-1">
                            <Input
                              type="number"
                              min="0"
                              max="8"
                              placeholder="ft"
                              value={formData.vitalsHeightFeet}
                              onChange={(e) =>
                                handleChange("vitalsHeightFeet", e.target.value)
                              }
                              className={inputCls}
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              type="number"
                              min="0"
                              max="11"
                              placeholder="in"
                              value={formData.vitalsHeightInches}
                              onChange={(e) =>
                                handleChange(
                                  "vitalsHeightInches",
                                  e.target.value,
                                )
                              }
                              className={inputCls}
                            />
                          </div>
                        </div>
                      )}
                      <Select
                        value={formData.vitalsHeightUnit}
                        onValueChange={(v) => {
                          handleChange("vitalsHeightUnit", v as any);
                          handleChange("vitalsHeight", "");
                          handleChange("vitalsHeightFeet", "");
                          handleChange("vitalsHeightInches", "");
                        }}
                      >
                        <SelectTrigger className={`w-24 ${selectCls}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={selectContentCls}>
                          <SelectItem value="cm">cm</SelectItem>
                          <SelectItem value="ft/in">ft/in</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Alert
                    className={`${
                      isDarkMode
                        ? "bg-blue-900/20 border-blue-800 text-blue-200"
                        : "bg-blue-50 border-blue-200 text-blue-900"
                    }`}
                  >
                    <Info className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      These readings will be automatically recorded as vitals in
                      the actions panel when the consultation is completed.
                    </AlertDescription>
                  </Alert>
                </div>
              </div>
            )}

          {/* ── Section 8: Supply Details (OC path) ──────────────────── */}
          {formData.serviceType === "oral-contraception" && (
            <div className={card}>
              <div className="flex items-center gap-3 mb-4">
                <div className={sectionBadge}>
                  {formData.ocPillType === "combined" ? 8 : 7}
                </div>
                <h3 className={sectionTitle}>Supply Details</h3>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className={labelCls}>
                    Has oral contraception been supplied?
                  </Label>
                  <RadioGroup
                    value={formData.ocSupplied}
                    onValueChange={(v) => {
                      handleChange("ocSupplied", v as any);
                      if (v === "yes") {
                        handleChange("noSupplyOutcome", "");
                        handleChange("noSupplyReason", "");
                        setEditingProduct({
                          supplyType: "pgd",
                          productPrescribed: "",
                          packDispensed: "",
                          numberOfPacks: "",
                          doseDuration: "",
                          daysSupply: "",
                          doseRegimenType: "",
                          tailoredDoseOption: "",
                          doseDirections:
                            "Take one tablet daily at the same time each day",
                          additionalInstructions: "",
                        });
                        setEditingProductIndex(null);
                        setIsProductPopupOpen(true);
                      }
                    }}
                    className="flex flex-col gap-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="yes"
                        id="oc-supplied-yes"
                        className={radioCls}
                      />
                      <label
                        htmlFor="oc-supplied-yes"
                        className={radioLabelCls}
                      >
                        Yes
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="no"
                        id="oc-supplied-no"
                        className={radioCls}
                      />
                      <label htmlFor="oc-supplied-no" className={radioLabelCls}>
                        No
                      </label>
                    </div>
                  </RadioGroup>
                </div>

                {formData.ocSupplied === "yes" && (
                  <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {/* Add Medication Button */}
                    <Button
                      type="button"
                      variant="outline"
                      className={`w-full ${
                        isDarkMode
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        setEditingProduct({
                          supplyType: "pgd",
                          productPrescribed: "",
                          packDispensed: "",
                          numberOfPacks: "",
                          doseDuration: "",
                          daysSupply: "",
                          doseRegimenType: "",
                          tailoredDoseOption: "",
                          doseDirections:
                            "Take one tablet daily at the same time each day",
                          additionalInstructions: "",
                        });
                        setEditingProductIndex(null);
                        setIsProductPopupOpen(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Medication Item
                    </Button>

                    {/* Medications List */}
                    {formData.suppliedProducts.length > 0 && (
                      <div className="space-y-3 mt-6">
                        <div className="flex items-center justify-between">
                          <h4
                            className={`text-sm font-semibold ${
                              isDarkMode ? "text-gray-200" : "text-gray-900"
                            }`}
                          >
                            Contraception Supply Medications (
                            {formData.suppliedProducts.length})
                          </h4>
                        </div>
                        <div className="space-y-3">
                          {formData.suppliedProducts.map((product, idx) => (
                            <div
                              key={idx}
                              className={`p-4 rounded-lg border-2 transition-all ${
                                isDarkMode
                                  ? "border-green-800 bg-green-950/20"
                                  : "border-green-200 bg-green-50/50"
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                  <div
                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                      isDarkMode
                                        ? "bg-green-900/50 text-green-300"
                                        : "bg-green-100 text-green-700"
                                    }`}
                                  >
                                    {idx + 1}
                                  </div>
                                </div>
                                <div className="flex-1 space-y-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <Pill
                                          className={`w-4 h-4 ${
                                            isDarkMode
                                              ? "text-green-400"
                                              : "text-green-600"
                                          }`}
                                        />
                                        <h5
                                          className={`text-sm font-semibold ${
                                            isDarkMode
                                              ? "text-gray-100"
                                              : "text-gray-900"
                                          }`}
                                        >
                                          {product.productPrescribed ||
                                            "No product selected"}
                                        </h5>
                                      </div>
                                      <span
                                        className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${
                                          isDarkMode
                                            ? "bg-green-900/40 text-green-300 border border-green-700"
                                            : "bg-green-100 text-green-700 border border-green-300"
                                        }`}
                                      >
                                        ✓ Supplied
                                      </span>
                                    </div>
                                    <div className="flex gap-1.5">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className={`h-8 px-3 text-xs ${
                                          isDarkMode
                                            ? "border-gray-600 hover:bg-gray-700 text-gray-300"
                                            : "border-gray-300 hover:bg-gray-100 text-gray-700"
                                        }`}
                                        title="Edit medication"
                                        onClick={() => {
                                          setEditingProduct({ ...product });
                                          setEditingProductIndex(idx);
                                          setIsProductPopupOpen(true);
                                        }}
                                      >
                                        <Edit2 className="w-3.5 h-3.5" />
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          const updated =
                                            formData.suppliedProducts.filter(
                                              (_, i) => i !== idx,
                                            );
                                          handleChange(
                                            "suppliedProducts",
                                            updated,
                                          );
                                          if (updated.length === 0)
                                            handleChange("ocSupplied", "");
                                        }}
                                        className="h-8 px-3 text-xs border-red-300 hover:bg-red-50 text-red-600 dark:border-red-700 dark:hover:bg-red-950/30 dark:text-red-400"
                                        title="Remove medication"
                                      >
                                        <Plus className="w-3.5 h-3.5 rotate-45" />
                                      </Button>
                                    </div>
                                  </div>

                                  <div className="space-y-2">
                                    <div className="grid grid-cols-2 gap-3">
                                      <div
                                        className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                      >
                                        <span
                                          className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                                        >
                                          Pack/Brand:
                                        </span>
                                        <div className="mt-0.5">
                                          {product.packDispensed || "—"}
                                        </div>
                                      </div>
                                      <div
                                        className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                      >
                                        <span
                                          className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                                        >
                                          Quantity:
                                        </span>
                                        <div className="mt-0.5">
                                          {product.numberOfPacks || "—"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div
                                        className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                      >
                                        <span
                                          className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                                        >
                                          Days Supply:
                                        </span>
                                        <div className="mt-0.5">
                                          {product.daysSupply
                                            ? `${product.daysSupply} days`
                                            : "—"}
                                        </div>
                                      </div>
                                      {product.doseDuration && (
                                        <div
                                          className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                        >
                                          <span
                                            className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                                          >
                                            Dose Duration:
                                          </span>
                                          <div className="mt-0.5">
                                            {product.doseDuration}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    <div
                                      className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                    >
                                      <span
                                        className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                                      >
                                        Dose Instructions:
                                      </span>
                                      <div
                                        className={`mt-1 p-2 rounded ${isDarkMode ? "bg-gray-800/50" : "bg-white/80"}`}
                                      >
                                        {product.doseDirections || "—"}
                                      </div>
                                    </div>
                                    {product.additionalInstructions && (
                                      <div
                                        className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                      >
                                        <span
                                          className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                                        >
                                          Additional Notes:
                                        </span>
                                        <div
                                          className={`mt-1 p-2 rounded italic ${isDarkMode ? "bg-gray-800/50" : "bg-white/80"}`}
                                        >
                                          {product.additionalInstructions}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {formData.ocSupplied === "no" && (
                  <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {/* No Supply Outcome */}
                    <div className="space-y-2">
                      <Label className={labelCls}>No supply outcome</Label>
                      <RadioGroup
                        value={formData.noSupplyOutcome}
                        onValueChange={(v) =>
                          handleChange("noSupplyOutcome", v)
                        }
                        className="flex flex-col gap-3"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="clinically-inappropriate"
                            id="no-supply-clinical"
                            className={radioCls}
                          />
                          <label
                            htmlFor="no-supply-clinical"
                            className={radioLabelCls}
                          >
                            Clinically inappropriate &amp; referred/signposted
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="declined-by-patient"
                            id="no-supply-declined"
                            className={radioCls}
                          />
                          <label
                            htmlFor="no-supply-declined"
                            className={radioLabelCls}
                          >
                            Declined by patient
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="other"
                            id="no-supply-other"
                            className={radioCls}
                          />
                          <label
                            htmlFor="no-supply-other"
                            className={radioLabelCls}
                          >
                            Other
                          </label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* No Supply Reason */}
                    <div className="space-y-2">
                      <Label className={labelCls}>No supply reason</Label>
                      <Select
                        value={formData.noSupplyReason}
                        onValueChange={(v) => handleChange("noSupplyReason", v)}
                      >
                        <SelectTrigger className={selectCls}>
                          <SelectValue placeholder="Select a reason" />
                        </SelectTrigger>
                        <SelectContent
                          className={`${selectContentCls} max-h-60`}
                        >
                          <SelectItem value="consent-not-given">
                            Consent not given
                          </SelectItem>
                          <SelectItem value="under-16-fraser">
                            Individuals under 16 years of age and assessed as
                            not competent using Fraser Guidelines
                          </SelectItem>
                          <SelectItem value="over-16-lacking-capacity">
                            Individuals 16 years of age and over and assessed as
                            lacking capacity to consent
                          </SelectItem>
                          <SelectItem value="known-suspected-pregnancy">
                            Known or suspected pregnancy
                          </SelectItem>
                          <SelectItem value="hypersensitivity">
                            Known hypersensitivity to the active ingredient or
                            to any constituent of the product
                          </SelectItem>
                          <SelectItem value="fewer-21-days-childbirth">
                            Fewer than 21 days after childbirth
                          </SelectItem>
                          <SelectItem value="fewer-5-days-miscarriage">
                            Fewer than 5 days after miscarriage, abortion,
                            ectopic pregnancy or uterine evacuation for
                            gestational trophoblastic disease (GTD)
                          </SelectItem>
                          <SelectItem value="breastfeeding-6-weeks">
                            Breastfeeding and less than six weeks postpartum
                          </SelectItem>
                          <SelectItem value="interacting-medicines">
                            Interacting medicines
                          </SelectItem>
                          <SelectItem value="cardiovascular">
                            Cardiovascular disease related
                          </SelectItem>
                          <SelectItem value="neurological">
                            Neurological condition related
                          </SelectItem>
                          <SelectItem value="cancer">Cancer related</SelectItem>
                          <SelectItem value="gastro-intestinal">
                            Gastro-intestinal condition related (including
                            malabsorption syndromes)
                          </SelectItem>
                          <SelectItem value="other-health-conditions">
                            Related to other health conditions
                          </SelectItem>
                          <SelectItem value="dvt-risk">
                            DVT risk concerns
                          </SelectItem>
                          <SelectItem value="above-target-bp-bmi">
                            Above target blood pressure or BMI
                          </SelectItem>
                          <SelectItem value="outside-pgd-age">
                            Patient outside approved PGD age range
                          </SelectItem>
                          <SelectItem value="acute-porphyria">
                            Acute porphyria
                          </SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Outcomes ──────────────────────────────────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>
                {(() => {
                  let n = 6;
                  if (formData.serviceType === "oral-contraception") n++; // OC Type section
                  if (formData.serviceType === "oral-contraception") n++; // Supply Details section
                  if (
                    formData.serviceType === "oral-contraception" &&
                    formData.ocPillType === "combined"
                  )
                    n++; // Vitals section
                  return n;
                })()}
              </div>
              <h3 className={sectionTitle}>Outcomes</h3>
            </div>

            <div className="space-y-5">
              {/* LARC discussed */}
              <div className="space-y-2">
                <Label className={labelCls}>
                  Has LARC or other effective contraception been discussed?
                </Label>
                <RadioGroup
                  value={formData.larcDiscussed}
                  onValueChange={(v) => handleChange("larcDiscussed", v as any)}
                  className="flex flex-col gap-3"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="yes"
                      id="larc-yes"
                      className={radioCls}
                    />
                    <label htmlFor="larc-yes" className={radioLabelCls}>
                      Yes
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="no"
                      id="larc-no"
                      className={radioCls}
                    />
                    <label htmlFor="larc-no" className={radioLabelCls}>
                      No
                    </label>
                  </div>
                </RadioGroup>
              </div>

              {/* Signposting / escalation required */}
              <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                <Label className={labelCls}>
                  Is signposting/escalation required?
                </Label>
                <RadioGroup
                  value={formData.signpostingRequired}
                  onValueChange={(v) => {
                    handleChange("signpostingRequired", v as any);
                    if (v === "no") {
                      handleChange("signpostingAction", "");
                      handleChange("referralDestination", "");
                      handleChange("referralDestinationOther", "");
                      handleChange("escalationDestination", "");
                      handleChange("escalationDestinationOther", "");
                      handleChange("onwardReferralReason", "");
                      handleChange("onwardReferralDate", "");
                      handleChange("onwardReferralTime", "");
                      handleChange("destinationOdsCode", "");
                    }
                  }}
                  className="flex flex-col gap-3"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="yes"
                      id="signpost-yes"
                      className={radioCls}
                    />
                    <label htmlFor="signpost-yes" className={radioLabelCls}>
                      Yes
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="no"
                      id="signpost-no"
                      className={radioCls}
                    />
                    <label htmlFor="signpost-no" className={radioLabelCls}>
                      No
                    </label>
                  </div>
                </RadioGroup>
              </div>

              {/* Which action is required */}
              {formData.signpostingRequired === "yes" && (
                <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <Label className={labelCls}>Which action is required?</Label>
                  <RadioGroup
                    value={formData.signpostingAction}
                    onValueChange={(v) => {
                      handleChange("signpostingAction", v as any);
                      if (v === "signposting") {
                        handleChange("escalationDestination", "");
                        handleChange("escalationDestinationOther", "");
                      }
                      if (v === "escalation") {
                        handleChange("referralDestination", "");
                        handleChange("referralDestinationOther", "");
                      }
                    }}
                    className="flex flex-col gap-3"
                  >
                    <label
                      htmlFor="action-signposting"
                      className={`flex items-start space-x-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                        formData.signpostingAction === "signposting"
                          ? isDarkMode
                            ? "border-blue-500 bg-blue-900/20"
                            : "border-blue-500 bg-blue-50"
                          : isDarkMode
                            ? "border-gray-600 bg-gray-700/30"
                            : "border-gray-200 bg-gray-50/50"
                      }`}
                    >
                      <RadioGroupItem
                        value="signposting"
                        id="action-signposting"
                        className={`mt-0.5 ${radioCls}`}
                      />
                      <span
                        className={`text-sm font-medium ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                      >
                        Signposting
                      </span>
                    </label>
                    <label
                      htmlFor="action-escalation"
                      className={`flex items-start space-x-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                        formData.signpostingAction === "escalation"
                          ? isDarkMode
                            ? "border-blue-500 bg-blue-900/20"
                            : "border-blue-500 bg-blue-50"
                          : isDarkMode
                            ? "border-gray-600 bg-gray-700/30"
                            : "border-gray-200 bg-gray-50/50"
                      }`}
                    >
                      <RadioGroupItem
                        value="escalation"
                        id="action-escalation"
                        className={`mt-0.5 ${radioCls}`}
                      />
                      <span
                        className={`text-sm font-medium ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                      >
                        Escalation
                      </span>
                    </label>
                  </RadioGroup>
                </div>
              )}

              {/* Signposting destination list */}
              {formData.signpostingRequired === "yes" &&
                formData.signpostingAction === "signposting" && (
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Label className={labelCls}>Signposting destination</Label>
                    <RadioGroup
                      value={formData.referralDestination}
                      onValueChange={(v) => {
                        handleChange("referralDestination", v);
                        if (v !== "other")
                          handleChange("referralDestinationOther", "");
                      }}
                      className="flex flex-col gap-3"
                    >
                      {[
                        { value: "gp-practice", label: "GP Practice" },
                        {
                          value: "sexual-health",
                          label: "Sexual Health Service",
                        },
                        { value: "out-of-hours-gp", label: "Out of hours GP" },
                        { value: "other", label: "Other" },
                      ].map(({ value, label }) => (
                        <div
                          key={value}
                          className="flex items-center space-x-2"
                        >
                          <RadioGroupItem
                            value={value}
                            id={`ref-${value}`}
                            className={radioCls}
                          />
                          <label
                            htmlFor={`ref-${value}`}
                            className={radioLabelCls}
                          >
                            {label}
                          </label>
                        </div>
                      ))}
                    </RadioGroup>
                    {formData.referralDestination === "other" && (
                      <div className="space-y-2 mt-3">
                        <Label className={labelCls}>Please specify</Label>
                        <Input
                          type="text"
                          placeholder="Specify destination"
                          value={formData.referralDestinationOther}
                          onChange={(e) =>
                            handleChange(
                              "referralDestinationOther",
                              e.target.value,
                            )
                          }
                          className={inputCls}
                        />
                      </div>
                    )}
                  </div>
                )}

              {/* Escalation destination list */}
              {formData.signpostingRequired === "yes" &&
                formData.signpostingAction === "escalation" && (
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <Label className={labelCls}>Escalation to</Label>
                    <RadioGroup
                      value={formData.escalationDestination}
                      onValueChange={(v) => {
                        handleChange("escalationDestination", v);
                        if (v !== "other")
                          handleChange("escalationDestinationOther", "");
                      }}
                      className="flex flex-col gap-3"
                    >
                      {[
                        { value: "gp-practice", label: "GP Practice" },
                        { value: "out-of-hours-gp", label: "Out of hours GP" },
                        {
                          value: "utc",
                          label: "UTC (Urgent Treatment Centre)",
                        },
                        { value: "999", label: "999" },
                        { value: "ae", label: "A&E" },
                        { value: "other", label: "Other" },
                      ].map(({ value, label }) => (
                        <div
                          key={value}
                          className="flex items-center space-x-2"
                        >
                          <RadioGroupItem
                            value={value}
                            id={`esc-${value}`}
                            className={radioCls}
                          />
                          <label
                            htmlFor={`esc-${value}`}
                            className={radioLabelCls}
                          >
                            {label}
                          </label>
                        </div>
                      ))}
                    </RadioGroup>
                    {formData.escalationDestination === "other" && (
                      <div className="space-y-2 mt-3">
                        <Label className={labelCls}>Please specify</Label>
                        <Input
                          type="text"
                          placeholder="Specify escalation destination"
                          value={formData.escalationDestinationOther}
                          onChange={(e) =>
                            handleChange(
                              "escalationDestinationOther",
                              e.target.value,
                            )
                          }
                          className={inputCls}
                        />
                      </div>
                    )}
                    {formData.escalationDestination && (
                      <div className="space-y-2 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <Label className={labelCls}>
                          Do you know the ODS code for the destination?
                        </Label>
                        <RadioGroup
                          value={knowsOdsCode}
                          onValueChange={(v) => {
                            setKnowsOdsCode(v);
                            if (v === "no")
                              handleChange("destinationOdsCode", "");
                          }}
                          className="flex flex-col gap-3"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="yes"
                              id="ods-yes"
                              className={radioCls}
                            />
                            <label htmlFor="ods-yes" className={radioLabelCls}>
                              Yes
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="no"
                              id="ods-no"
                              className={radioCls}
                            />
                            <label htmlFor="ods-no" className={radioLabelCls}>
                              No
                            </label>
                          </div>
                        </RadioGroup>
                        {knowsOdsCode === "yes" && (
                          <div className="space-y-2 mt-3">
                            <Label htmlFor="ods-code" className={labelCls}>
                              ODS Code
                            </Label>
                            <Input
                              id="ods-code"
                              type="text"
                              placeholder="Enter ODS code"
                              value={formData.destinationOdsCode}
                              onChange={(e) =>
                                handleChange(
                                  "destinationOdsCode",
                                  e.target.value,
                                )
                              }
                              className={inputCls}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>

          {/* ── Section 8: Advice and Support ───────────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>
                {(() => {
                  let base = 7;
                  if (formData.serviceType === "oral-contraception") base++; // OC Type
                  if (formData.serviceType === "oral-contraception") base++; // Supply Details
                  if (
                    formData.serviceType === "oral-contraception" &&
                    formData.ocPillType === "combined"
                  )
                    base++; // Vitals
                  return base;
                })()}
              </div>
              <h3 className={sectionTitle}>Advice and Support</h3>
            </div>

            <div className="space-y-4">
              <Alert
                className={`${
                  isDarkMode
                    ? "bg-blue-900/20 border-blue-800 text-blue-200"
                    : "bg-blue-50 border-blue-200 text-blue-900"
                }`}
              >
                <Info className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  Ensure the patient has been provided with appropriate advice
                  regarding their contraception, including correct usage, missed
                  dose guidance, and when to seek further support.
                </AlertDescription>
              </Alert>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="advice-given"
                  checked={formData.adviceGiven}
                  onCheckedChange={(checked) =>
                    handleChange("adviceGiven", checked === true)
                  }
                  className={`${
                    isDarkMode
                      ? "border-gray-600 data-[state=checked]:bg-blue-600"
                      : "border-gray-300 data-[state=checked]:bg-blue-600"
                  }`}
                />
                <label
                  htmlFor="advice-given"
                  className={`text-sm cursor-pointer ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Patient has been given this advice
                </label>
              </div>

              <div className="space-y-2">
                <Label className={labelCls}>
                  Who was the advice provided to?
                </Label>
                <RadioGroup
                  value={formData.adviceProvidedTo}
                  onValueChange={(v) => handleChange("adviceProvidedTo", v)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="patient"
                      id="advice-patient"
                      className={radioCls}
                    />
                    <label htmlFor="advice-patient" className={radioLabelCls}>
                      Patient
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="advocate"
                      id="advice-advocate"
                      className={radioCls}
                    />
                    <label htmlFor="advice-advocate" className={radioLabelCls}>
                      Patient's advocate
                    </label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </div>

          {/* ── Section 9: Notes ─────────────────────────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>
                {(() => {
                  let base = 8;
                  if (formData.serviceType === "oral-contraception") base++; // OC Type
                  if (formData.serviceType === "oral-contraception") base++; // Supply Details
                  if (
                    formData.serviceType === "oral-contraception" &&
                    formData.ocPillType === "combined"
                  )
                    base++; // Vitals
                  return base;
                })()}
              </div>
              <h3 className={sectionTitle}>Notes</h3>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="plan-actions"
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Plan &amp; requested actions{" "}
                  <span className="text-gray-400">(Optional)</span>
                </Label>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  This will be sent to the GP but MUST NOT be used for
                  communicating actions. Any actions must be communicated by
                  usual business routes.
                </p>
                <Textarea
                  id="plan-actions"
                  placeholder="Enter plan and requested actions..."
                  value={formData.planAndRequestedActions}
                  onChange={(e) =>
                    handleChange("planAndRequestedActions", e.target.value)
                  }
                  rows={4}
                  className={inputCls}
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="internal-notes"
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Internal notes{" "}
                  <span className="text-gray-400">(Optional)</span>
                </Label>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  This data will not flow to GP practices, and should be used
                  for any notes you want to keep for your own internal pharmacy
                  records.
                </p>
                <Textarea
                  id="internal-notes"
                  placeholder="Enter internal notes..."
                  value={formData.internalNotes}
                  onChange={(e) =>
                    handleChange("internalNotes", e.target.value)
                  }
                  rows={4}
                  className={inputCls}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Data Sharing Details Dialog ──────────────────────────────────── */}
      <Dialog
        open={isDataSharingPopupOpen}
        onOpenChange={setIsDataSharingPopupOpen}
      >
        <DialogContent
          className={`max-w-2xl ${
            isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white"
          }`}
        >
          <DialogHeader>
            <DialogTitle
              className={`text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Data Sharing Details
            </DialogTitle>
          </DialogHeader>
          <div
            className={`space-y-4 ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            <ul className="space-y-3 list-none">
              {[
                "Consent is required for service provision.",
                "If the patient / their advocate gives consent for data sharing, the information they provide will be passed to the patient's GP practice, NHS England and the NHS BSA.",
                "Consent is usually from the patient but may be from an advocate who presents on behalf of or with the patient.",
                "If obtaining consent from a young person under the age of 16, please consider Fraser / Gillick competency before proceeding.",
              ].map((text) => (
                <li key={text} className="flex gap-3">
                  <span className="text-blue-600 dark:text-blue-400 font-bold flex-shrink-0">
                    •
                  </span>
                  <span className="text-sm">{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Complete Button ───────────────────────────────────────────────── */}
      <div className="flex justify-end pt-4">
        <Button
          type="submit"
          disabled={nhsContraceptionFormLoading || consultationStatus === 1}
          className={`${
            isDarkMode
              ? "bg-white text-black hover:bg-gray-200"
              : "bg-black text-white hover:bg-gray-800"
          }`}
        >
          {nhsContraceptionFormLoading ? "Please wait..." : "Complete"}
        </Button>
      </div>
      {/* ── Add Contraception Product Popup ─────────────────────────── */}
      <Dialog open={isProductPopupOpen} onOpenChange={setIsProductPopupOpen}>
        <DialogContent
          className={`max-w-lg max-h-[90vh] overflow-y-auto ${
            isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white"
          }`}
        >
          <DialogHeader>
            <DialogTitle
              className={`text-base font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {editingProductIndex !== null
                ? "Edit Contraception Product"
                : "Add Contraception Product"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            {/* Product Supply Type */}
            <div className="space-y-2">
              <Label className={labelCls}>Product Supply Type</Label>
              <div
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border ${
                  isDarkMode
                    ? "border-blue-600 bg-blue-900/20"
                    : "border-blue-300 bg-blue-50"
                }`}
              >
                <RadioGroup
                  value={editingProduct.supplyType}
                  onValueChange={(v) =>
                    setEditingProduct((p) => ({ ...p, supplyType: v }))
                  }
                  className="flex flex-col gap-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="pgd"
                      id="supply-pgd"
                      className={radioCls}
                    />
                    <label htmlFor="supply-pgd" className={radioLabelCls}>
                      Patient Group Direction (PGD)
                    </label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Product Prescribed */}
            <div className="space-y-2">
              <Label className={labelCls}>
                {formData.ocPillType === "progestogen-only"
                  ? "Progestogen Only Pill product prescribed"
                  : formData.ocPillType === "combined"
                    ? "Combined Oral Contraceptive product prescribed"
                    : "Product prescribed"}
              </Label>
              <Select
                value={editingProduct.productPrescribed}
                onValueChange={(v) =>
                  setEditingProduct((p) => ({
                    ...p,
                    productPrescribed: v,
                    packDispensed: "",
                  }))
                }
              >
                <SelectTrigger className={selectCls}>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent className={selectContentCls}>
                  {formData.ocPillType === "progestogen-only" ? (
                    <>
                      <SelectItem value="Zelleta 75microgram tablets">
                        Zelleta 75microgram tablets
                      </SelectItem>
                      <SelectItem value="Cerazette 75microgram tablets">
                        Cerazette 75microgram tablets
                      </SelectItem>
                      <SelectItem value="Cerelle 75microgram tablets">
                        Cerelle 75microgram tablets
                      </SelectItem>
                      <SelectItem value="Norgeston 30microgram tablets">
                        Norgeston 30microgram tablets
                      </SelectItem>
                      <SelectItem value="Noriday 350microgram tablets">
                        Noriday 350microgram tablets
                      </SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="Microgynon 30 tablets">
                        Microgynon 30 tablets
                      </SelectItem>
                      <SelectItem value="Rigevidon tablets">
                        Rigevidon tablets
                      </SelectItem>
                      <SelectItem value="Levest tablets">
                        Levest tablets
                      </SelectItem>
                      <SelectItem value="Cilique tablets">
                        Cilique tablets
                      </SelectItem>
                      <SelectItem value="Yasmin tablets">
                        Yasmin tablets
                      </SelectItem>
                      <SelectItem value="Lucette tablets">
                        Lucette tablets
                      </SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Pack dispensed */}
            <div className="space-y-2">
              <Label className={labelCls}>Pack dispensed</Label>
              <Select
                value={editingProduct.packDispensed}
                onValueChange={(v) =>
                  setEditingProduct((p) => ({ ...p, packDispensed: v }))
                }
              >
                <SelectTrigger className={selectCls}>
                  <SelectValue placeholder="Select pack" />
                </SelectTrigger>
                <SelectContent className={selectContentCls}>
                  {editingProduct.productPrescribed ? (
                    <>
                      <SelectItem
                        value={`${editingProduct.productPrescribed} 84 tablet 3 x 28 tablets`}
                      >
                        {editingProduct.productPrescribed} 84 tablet 3 x 28
                        tablets
                      </SelectItem>
                      <SelectItem
                        value={`${editingProduct.productPrescribed} 63 tablet 3 x 21 tablets`}
                      >
                        {editingProduct.productPrescribed} 63 tablet 3 x 21
                        tablets
                      </SelectItem>
                    </>
                  ) : (
                    <SelectItem value="__placeholder" disabled>
                      Select a product first
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Number of packs / Dose duration / Days supply */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className={labelCls}>Packs supplied</Label>
                <Input
                  type="number"
                  min="1"
                  placeholder=""
                  value={editingProduct.numberOfPacks}
                  onChange={(e) =>
                    setEditingProduct((p) => ({
                      ...p,
                      numberOfPacks: e.target.value,
                    }))
                  }
                  className={`${inputCls} border-red-400`}
                />
                <span className="text-xs text-red-500">Required</span>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Duration</Label>
                <Select
                  value={editingProduct.doseDuration}
                  onValueChange={(v) =>
                    setEditingProduct((p) => ({ ...p, doseDuration: v }))
                  }
                >
                  <SelectTrigger className={`${selectCls} border-red-400`}>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    <SelectItem value="1 month">1 month</SelectItem>
                    <SelectItem value="2 months">2 months</SelectItem>
                    <SelectItem value="3 months">3 months</SelectItem>
                    <SelectItem value="6 months">6 months</SelectItem>
                    <SelectItem value="12 months">12 months</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-red-500">Required</span>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Days</Label>
                <Input
                  type="number"
                  min="1"
                  placeholder=""
                  value={editingProduct.daysSupply}
                  onChange={(e) =>
                    setEditingProduct((p) => ({
                      ...p,
                      daysSupply: e.target.value,
                    }))
                  }
                  className={`${inputCls} border-red-400`}
                />
                <span className="text-xs text-red-500">Required</span>
              </div>
            </div>

            {editingProduct.numberOfPacks && (
              <p
                className={`text-xs ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {editingProduct.numberOfPacks} pack(s) equivalent to tablets
              </p>
            )}

            {/* Dose Regimen Type (Combined OC only) */}
            {formData.ocPillType === "combined" && (
              <div className="space-y-3">
                <Label className={labelCls}>Dose Regimen Type</Label>
                <RadioGroup
                  value={editingProduct.doseRegimenType}
                  onValueChange={(v) => {
                    setEditingProduct((p) => ({
                      ...p,
                      doseRegimenType: v,
                      tailoredDoseOption: "",
                      doseDirections:
                        v === "standard"
                          ? "Take one tablet daily at the same time each day"
                          : p.doseDirections,
                    }));
                  }}
                  className="flex flex-col gap-3"
                >
                  <label
                    htmlFor="regimen-standard"
                    className={`flex items-start space-x-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                      editingProduct.doseRegimenType === "standard"
                        ? isDarkMode
                          ? "border-blue-500 bg-blue-900/20"
                          : "border-blue-500 bg-blue-50"
                        : isDarkMode
                          ? "border-gray-600 bg-gray-700/30"
                          : "border-gray-200 bg-gray-50/50"
                    }`}
                  >
                    <RadioGroupItem
                      value="standard"
                      id="regimen-standard"
                      className={`mt-0.5 ${radioCls}`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-100" : "text-gray-900"
                      }`}
                    >
                      Standard dose regimen
                    </span>
                  </label>
                  <label
                    htmlFor="regimen-tailored"
                    className={`flex items-start space-x-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                      editingProduct.doseRegimenType === "tailored"
                        ? isDarkMode
                          ? "border-blue-500 bg-blue-900/20"
                          : "border-blue-500 bg-blue-50"
                        : isDarkMode
                          ? "border-gray-600 bg-gray-700/30"
                          : "border-gray-200 bg-gray-50/50"
                    }`}
                  >
                    <RadioGroupItem
                      value="tailored"
                      id="regimen-tailored"
                      className={`mt-0.5 ${radioCls}`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-100" : "text-gray-900"
                      }`}
                    >
                      Tailored dose regimen
                    </span>
                  </label>
                </RadioGroup>

                {/* Tailored dose options */}
                {editingProduct.doseRegimenType === "tailored" && (
                  <div className="space-y-2 pt-2">
                    <Label className={labelCls}>Dose Direction</Label>
                    <RadioGroup
                      value={editingProduct.tailoredDoseOption}
                      onValueChange={(v) => {
                        const directionMap: Record<string, string> = {
                          "21-4-short":
                            "Take one tablet each day for 21 days and then take a 4 day break",
                          "63-7-tricycle":
                            "Take one tablet each day for 63 days and then take a 7 day break",
                          "63-4-tricycle":
                            "Take one tablet each day for 63 days and then take a 4 day break",
                          "breakthrough-flexible":
                            "Take one tablet every day until breakthrough bleeding occurs for 3 or 4 days and then take a 4 day break",
                          continuous:
                            "Take one tablet every day with no breaks",
                        };
                        setEditingProduct((p) => ({
                          ...p,
                          tailoredDoseOption: v,
                          doseDirections: directionMap[v] || p.doseDirections,
                        }));
                      }}
                      className="flex flex-col gap-2"
                    >
                      <label
                        htmlFor="tailored-21-4"
                        className={`flex items-start space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                          editingProduct.tailoredDoseOption === "21-4-short"
                            ? isDarkMode
                              ? "border-blue-500 bg-blue-900/20"
                              : "border-blue-500 bg-blue-50"
                            : isDarkMode
                              ? "border-gray-600 bg-gray-700/30"
                              : "border-gray-200 bg-gray-50/50"
                        }`}
                      >
                        <RadioGroupItem
                          value="21-4-short"
                          id="tailored-21-4"
                          className={`mt-0.5 ${radioCls}`}
                        />
                        <div>
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                          >
                            Take one tablet each day for 21 days and then take a
                            4 day break
                          </span>
                          <p
                            className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Short Break
                          </p>
                        </div>
                      </label>

                      <label
                        htmlFor="tailored-63-7"
                        className={`flex items-start space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                          editingProduct.tailoredDoseOption === "63-7-tricycle"
                            ? isDarkMode
                              ? "border-blue-500 bg-blue-900/20"
                              : "border-blue-500 bg-blue-50"
                            : isDarkMode
                              ? "border-gray-600 bg-gray-700/30"
                              : "border-gray-200 bg-gray-50/50"
                        }`}
                      >
                        <RadioGroupItem
                          value="63-7-tricycle"
                          id="tailored-63-7"
                          className={`mt-0.5 ${radioCls}`}
                        />
                        <div>
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                          >
                            Take one tablet each day for 63 days and then take a
                            7 day break
                          </span>
                          <p
                            className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Tri-cycling / Extended use
                          </p>
                        </div>
                      </label>

                      <label
                        htmlFor="tailored-63-4"
                        className={`flex items-start space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                          editingProduct.tailoredDoseOption === "63-4-tricycle"
                            ? isDarkMode
                              ? "border-blue-500 bg-blue-900/20"
                              : "border-blue-500 bg-blue-50"
                            : isDarkMode
                              ? "border-gray-600 bg-gray-700/30"
                              : "border-gray-200 bg-gray-50/50"
                        }`}
                      >
                        <RadioGroupItem
                          value="63-4-tricycle"
                          id="tailored-63-4"
                          className={`mt-0.5 ${radioCls}`}
                        />
                        <div>
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                          >
                            Take one tablet each day for 63 days and then take a
                            4 day break
                          </span>
                          <p
                            className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Tri-cycling / Extended use
                          </p>
                        </div>
                      </label>

                      <label
                        htmlFor="tailored-breakthrough"
                        className={`flex items-start space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                          editingProduct.tailoredDoseOption ===
                          "breakthrough-flexible"
                            ? isDarkMode
                              ? "border-blue-500 bg-blue-900/20"
                              : "border-blue-500 bg-blue-50"
                            : isDarkMode
                              ? "border-gray-600 bg-gray-700/30"
                              : "border-gray-200 bg-gray-50/50"
                        }`}
                      >
                        <RadioGroupItem
                          value="breakthrough-flexible"
                          id="tailored-breakthrough"
                          className={`mt-0.5 ${radioCls}`}
                        />
                        <div>
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                          >
                            Take one tablet every day until breakthrough
                            bleeding occurs for 3 or 4 days and then take a 4
                            day break
                          </span>
                          <p
                            className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Flexible extended use
                          </p>
                        </div>
                      </label>

                      <label
                        htmlFor="tailored-continuous"
                        className={`flex items-start space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                          editingProduct.tailoredDoseOption === "continuous"
                            ? isDarkMode
                              ? "border-blue-500 bg-blue-900/20"
                              : "border-blue-500 bg-blue-50"
                            : isDarkMode
                              ? "border-gray-600 bg-gray-700/30"
                              : "border-gray-200 bg-gray-50/50"
                        }`}
                      >
                        <RadioGroupItem
                          value="continuous"
                          id="tailored-continuous"
                          className={`mt-0.5 ${radioCls}`}
                        />
                        <div>
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                          >
                            Take one tablet every day with no breaks
                          </span>
                          <p
                            className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Continuous use
                          </p>
                        </div>
                      </label>
                    </RadioGroup>
                  </div>
                )}
              </div>
            )}

            {/* Dose directions */}
            <div className="space-y-2">
              <Label className={labelCls}>Dose directions</Label>
              <Textarea
                rows={3}
                value={editingProduct.doseDirections}
                onChange={(e) =>
                  setEditingProduct((p) => ({
                    ...p,
                    doseDirections: e.target.value,
                  }))
                }
                className={inputCls}
              />
            </div>

            {/* Additional instructions */}
            <div className="space-y-2">
              <Label className={labelCls}>
                Additional instructions{" "}
                <span
                  className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                >
                  (Optional)
                </span>
              </Label>
              <Textarea
                rows={3}
                value={editingProduct.additionalInstructions}
                onChange={(e) =>
                  setEditingProduct((p) => ({
                    ...p,
                    additionalInstructions: e.target.value,
                  }))
                }
                className={inputCls}
                placeholder=""
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsProductPopupOpen(false);
                setEditingProductIndex(null);
                // If no products were added yet, reset ocSupplied
                if (formData.suppliedProducts.length === 0) {
                  handleChange("ocSupplied", "");
                }
              }}
              className={
                isDarkMode
                  ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                  : ""
              }
            >
              CANCEL
            </Button>
            <Button
              type="button"
              disabled={
                !editingProduct.productPrescribed ||
                !editingProduct.numberOfPacks ||
                !editingProduct.doseDuration ||
                !editingProduct.daysSupply
              }
              onClick={() => {
                if (editingProductIndex !== null) {
                  const updated = [...formData.suppliedProducts];
                  updated[editingProductIndex] = { ...editingProduct };
                  handleChange("suppliedProducts", updated);
                } else {
                  handleChange("suppliedProducts", [
                    ...formData.suppliedProducts,
                    { ...editingProduct },
                  ]);
                }
                setEditingProductIndex(null);
                setIsProductPopupOpen(false);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {editingProductIndex !== null ? "UPDATE" : "SUBMIT"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </form>
  );
}
